import React, { useState, useEffect } from 'react';
import { FileText, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { letterApi, letterRegApi } from '../lib/api';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';

export default function LetterHistory() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    letterApi.list().then((r) => setRows(r.data)).catch(() => toast.error('Failed to load letters')).finally(() => setLoading(false));
  }, []);

  const reprint = async (r) => {
    try {
      const LOGO_URL = window.location.origin + '/assets/aampl-logo.jpg';
      // Dispatch by source — all non-mutating so reprinting no longer creates duplicates.
      let res;
      if (r.source === 'promotion' || r.kind === 'Promotion') res = await letterRegApi.reprintPromotion(r.id, { logo_url: LOGO_URL });
      else if (r.source === 'transfer' || r.kind === 'Transfer' || r.kind === 'Department Reassignment') res = await letterRegApi.reprintTransfer(r.id, { logo_url: LOGO_URL });
      else if (r.source === 'employee_letter') res = await letterRegApi.print(r.id, { logo_url: LOGO_URL });
      else if (r.source === 'offer' || r.kind === 'Offer') res = await letterRegApi.reprintOffer(r.id, { logo_url: LOGO_URL });
      else { toast.error('Unknown letter type'); return; }
      const w = window.open('', '_blank'); if (!w) { toast.error('Allow pop-ups'); return; }
      w.document.open(); w.document.write(res.data.html); w.document.close();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Reprint failed'); }
  };

  const remove = async (r) => {
    const kind = r.kind === 'Promotion' ? 'promotion' : 'transfer';
    const warn = kind === 'promotion'
      ? `Delete this promotion record for ${r.employee_name}? It disappears from the register, the employee's history AND future cycle letter runs. The designation change itself is NOT reverted.`
      : `Delete this transfer letter record for ${r.employee_name}? It disappears from the register and the employee's history. The transfer itself is NOT reverted.`;
    if (!window.confirm(warn)) return;
    try {
      await letterApi.removeRegister(kind, r.id);
      setRows((s) => s.filter((x) => x.id !== r.id));
      toast.success('Letter record deleted');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div data-testid="letter-history-page" className="flex h-screen app-shell">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Header />
        <main className="flex-1 overflow-auto p-5 sm:p-6 lg:p-8 page-enter">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex items-center gap-3 mb-2"><FileText className="w-6 h-6 text-[#5A6377]" /><h1 className="font-display text-3xl sm:text-4xl tracking-[-0.035em] font-extrabold text-[#172554]">Letter History</h1></div>
            <p className="text-sm text-[#5A6377] mb-6">{rows.length} letters issued. Reprint regenerates the document from the saved details.</p>
            {loading ? <div className="text-sm text-[#5A6377]">Loading…</div> : (
              <div className="bg-white border border-[#E6E9F0] rounded-xl overflow-hidden">
                <table className="w-full">
                  <thead className="bg-[#102468] text-white text-xs font-semibold uppercase tracking-wide">
                    <tr><th className="py-3 px-4 text-left">Date</th><th className="py-3 px-4 text-left">Type</th><th className="py-3 px-4 text-left">Employee</th><th className="py-3 px-4 text-left">Change</th><th className="py-3 px-4 text-left">Effective</th><th className="py-3 px-4 text-left">By</th><th className="py-3 px-4 text-left"></th></tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={`${r.kind}-${r.id}`} className="border-b border-[#E6E9F0] last:border-0 hover:bg-[#F7F8FB]">
                        <td className="py-3 px-4 text-xs text-[#98A1B2]">{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</td>
                        <td className="py-3 px-4"><span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${r.kind === 'Promotion' ? 'bg-[#FBF7EC] text-[#8A5A12]' : 'bg-[#EAF4FE] text-[#15549E]'}`}>{r.kind}</span></td>
                        <td className="py-3 px-4 text-sm font-medium text-[#11182B]">{r.employee_name}</td>
                        <td className="py-3 px-4 text-sm text-[#5A6377]">{r.detail}</td>
                        <td className="py-3 px-4 text-sm text-[#5A6377]">{r.effective_date}</td>
                        <td className="py-3 px-4 text-xs text-[#98A1B2]">{r.issued_by_name || '—'}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2 justify-end">
                            <Button variant="outline" size="sm" onClick={() => reprint(r)}>Reprint</Button>
                            <Button data-testid={`letter-delete-${r.id}`} variant="outline" size="sm"
                              className="border-[#F5C9CC] text-[#C2151F] hover:bg-[#FDE7E9]" onClick={() => remove(r)}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {rows.length === 0 && <tr><td colSpan={7} className="py-12 text-center text-[#98A1B2]">No letters issued yet</td></tr>}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
